import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { tryAutoReloadOnChunkError } from '@/lib/lazy-with-reload';

/**
 * Globaler Fehler-Fang.
 *
 * React unterbricht bei unfangenem JS-Fehler in einem Render die gesamte
 * Komponenten-Unterbaum. Ohne ErrorBoundary sieht der User eine weiße Seite.
 * Die Boundary fängt Render-Fehler und zeigt eine verständliche Meldung
 * mit „Seite neu laden"-Button.
 *
 * Fehlt-NICHT-fängt:
 *   - Event-Handler (onClick etc.) — dort direkt try/catch
 *   - Async-Code / Promises — fängt React-Query ab
 *   - Fehler beim Server-Side-Rendering — nicht relevant, avameo ist CSR
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Stale-Chunk-Fall: schon hier reloaden, damit der User keine Error-UI
    // zu sehen bekommt. tryAutoReloadOnChunkError ist debounced (10 s
    // sessionStorage), Loop-sicher. Side-Effect in einer statischen Lifecycle-
    // Methode ist hier vertretbar — Reload ist quasi "navigate away".
    if (tryAutoReloadOnChunkError(error)) return { error: null };
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Sicherheitsnetz: wenn getDerivedStateFromError den Reload nicht
    // ausgeloest hat (z.B. weil Pattern erst hier matchen), nochmal probieren.
    if (tryAutoReloadOnChunkError(error)) return;

    // In Production würde das an ein Monitoring-System gehen (Sentry etc.)

    console.error('ErrorBoundary caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleReset = (): void => {
    this.setState({ error: null, errorInfo: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg-alt p-6">
          <div className="w-full max-w-lg border border-hair bg-panel p-8">
            <div className="mb-4 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              <h1 className="text-lg font-semibold text-ink">Unerwarteter Fehler</h1>
            </div>
            <p className="mb-4 text-sm text-ink-soft">
              Die Anwendung hat einen unerwarteten Fehler festgestellt. Das ist uns peinlich. Du
              kannst es mit einem Reload versuchen — deine bisherigen Eingaben gehen allerdings
              verloren.
            </p>
            <details className="mb-4 border border-hair bg-bg-alt p-3">
              <summary className="cursor-pointer text-xs font-medium text-ink-muted">
                Technische Details
              </summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-[10px] text-ink-muted">
                {this.state.error.message}
                {this.state.error.stack && `\n\n${this.state.error.stack.slice(0, 500)}…`}
              </pre>
            </details>
            <div className="flex gap-2">
              <Button onClick={this.handleReload}>
                <RefreshCw className="h-4 w-4" />
                Seite neu laden
              </Button>
              <Button variant="outline" onClick={this.handleReset}>
                Weiter versuchen
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
